"use client"

import { useState, useRef } from "react"
import { motion, useScroll, useTransform } from "framer-motion"
import { Button } from "./ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

const designerPicks = [
  {
    id: "pick1",
    name: "Rochor Wood Dining Table",
    note: "Exquisite craftsmanship meets timeless design in this stunning dining centerpiece.",
    image: "/FndrsPick_1.png",
    price: "₹8,70,000",
    url: "/product-luxury/rochor-wood-dining-table-2",
  },
  {
    id: "pick2",
    name: "Zep the dog",
    note: "A charming companion piece that brings personality to any space.",
    image: "/FndrsPick_2.png",
    price: "₹6,000",
    url: "/product-luxury/zep-the-dog-2",
  },
  {
    id: "pick3",
    name: "Spheria Coffee table",
    note: "Contemporary elegance with clean lines and sophisticated appeal.",
    image: "/FndrsPick_3.png",
    price: "₹1,10,000",
    url: "/product-luxury/spheria-coffee-table-2",
  },
  {
    id: "pick4",
    name: "Nora Lamp (L) - Zebra Marble",
    note: "Luxurious marble craftsmanship that illuminates with distinctive style.",
    image: "/FndrsPick_4.png",
    price: "₹8,700",
    url: "/product-luxury/nora-lamp-l-zebra-marble-2",
  },
  {
    id: "pick5",
    name: "Cuddle Cloud",
    note: "Supreme comfort meets contemporary design in this plush seating solution.",
    image: "/FndrsPick_5.png",
    price: "₹56,000",
    url: "/product-luxury/cuddle-cloud",
  },
]

export default function DesignerPicks() {
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef(null)
  const { scrollXProgress } = useScroll({ container: containerRef })

  const nextSlide = () => {
    if (activeIndex < designerPicks.length - 1) {
      setActiveIndex(activeIndex + 1)
      containerRef.current?.scrollTo({
        left: (activeIndex + 1) * (containerRef.current.scrollWidth / designerPicks.length),
        behavior: "smooth",
      })
    }
  }

  const prevSlide = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1)
      containerRef.current?.scrollTo({
        left: (activeIndex - 1) * (containerRef.current.scrollWidth / designerPicks.length),
        behavior: "smooth",
      })
    }
  }

  const handlePickClick = (pick) => {
    window.open(pick.url, '_blank')
  }

  return (
    <section className="bg-stone-50 py-24">
      <div className="container mx-auto px-4">
        <motion.h2
          className="mb-4 text-center font-serif text-3xl font-light md:text-4xl"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Curated by Our Founders
        </motion.h2>

        <motion.p
          className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Handpicked favorites from those who know our collection best.
        </motion.p>

        <div className="relative">
          {/* Desktop Carousel */}
          <div ref={containerRef} className="hide-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto">
            {designerPicks.map((pick, index) => (
              <motion.div
                key={pick.id}
                className="relative flex h-[60vh] w-[80vw] max-w-5xl flex-shrink-0 snap-center flex-col justify-end overflow-hidden cursor-pointer md:w-[60vw]"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => handlePickClick(pick)}
              >
                <img src={pick.image || "/placeholder.svg"} alt={pick.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

                <div className="relative z-10 p-8 text-white">
                  <div className="mb-2 inline-block transform -rotate-2 bg-amber-700 px-3 py-1 text-xs font-medium uppercase tracking-wider">
                    Designer Pick
                  </div>
                  <h3 className="mb-2 font-serif text-3xl font-bold text-white">{pick.name}</h3>
                  <p className="mb-4 max-w-md italic">"{pick.note}"</p>
                  <p className="mb-6 text-xl font-light">{pick.price}</p>
                  <Button 
                    className="rounded-none bg-white text-stone-900 hover:bg-stone-100"
                    onClick={(e) => {
                      e.stopPropagation()
                      handlePickClick(pick)
                    }}
                  >
                    View Product
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Carousel Controls */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 z-20 h-12 w-12 -translate-y-1/2 rounded-full bg-white/70 text-stone-800 backdrop-blur-sm hover:bg-white/90"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              if (activeIndex > 0) {
                prevSlide()
              }
            }}
            disabled={activeIndex === 0}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 z-20 h-12 w-12 -translate-y-1/2 rounded-full bg-white/70 text-stone-800 backdrop-blur-sm hover:bg-white/90"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              if (activeIndex < designerPicks.length - 1) {
                nextSlide()
              }
            }}
            disabled={activeIndex === designerPicks.length - 1}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>

          {/* Invisible overlays to block clicks when arrows are disabled */}
          {activeIndex === 0 && (
            <div 
              className="absolute left-0 top-1/2 z-30 h-16 w-16 -translate-y-1/2 cursor-not-allowed"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            />
          )}
          {activeIndex === designerPicks.length - 1 && (
            <div 
              className="absolute right-0 top-1/2 z-30 h-16 w-16 -translate-y-1/2 cursor-not-allowed"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            />
          )}
        </div>

        {/* Progress Bar */}
        <div className="mx-auto mt-6 h-[2px] w-48 bg-stone-300">
          <motion.div
            className="h-full bg-amber-800"
            style={{ width: useTransform(scrollXProgress, [0, 1], ["0%", "100%"]) }}
          />
        </div>

      </div>
    </section>
  )
} 